/**
 * Who may **approve / reject** a UJP and edit the per-client UJP config
 * (`/ujp/config`).
 *
 * `REACT_APP_UJP_APPROVER_EMAILS`: comma-separated addresses, case- and
 * whitespace-insensitive. Unset / empty means **nobody** — the allowlist fails
 * closed on purpose, mirroring the service's `UJP_APPROVER_EMAILS` (an empty
 * server allowlist also lets nobody approve). Unlike the zone editor list
 * there is no `all` escape hatch: finance authorizes cash with this click.
 *
 * ⚠️ This is a UI affordance, not a security boundary. The bundle is public and
 * the value is baked in at build time, so anyone can read the list and call the
 * API directly. Real enforcement is the service's allowlist check on
 * `POST /v1/ujp/:id/decision` and `PUT /v1/ujp/client-configs/:clientId`
 * (403 for a non-approver, the requester deciding their own request, or a
 * token without an email claim). The detail response also carries
 * `viewer.canDecide`, which the panel prefers over this list once loaded.
 */

const RAW_ALLOWLIST = process.env.REACT_APP_UJP_APPROVER_EMAILS ?? '';

const parseAllowlist = (raw: string): Set<string> =>
  new Set(
    raw
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
  );

const APPROVERS = parseAllowlist(RAW_ALLOWLIST);

/**
 * UJP managers (R3): may create/update AND approve — including deciding their
 * OWN UJP (the self-approval guard is skipped for them server-side). Mirrors
 * the service's `UJP_MANAGER_EMAILS`. Same UI-affordance caveat as above; the
 * server `viewer.canDecide` is authoritative once loaded.
 */
const MANAGERS = parseAllowlist(process.env.REACT_APP_UJP_MANAGER_EMAILS ?? '');

/** May this user approve / reject UJPs and edit client UJP configs? */
export const isUjpApprover = (email?: string | null): boolean => {
  if (!email) return false;
  return APPROVERS.has(email.trim().toLowerCase());
};

/**
 * May this user act as a UJP manager (decide their own UJP included)? The
 * decide buttons themselves are driven by the server `viewer.canDecide` (which
 * already accounts for managers), so this is for UI hints / a "who can decide"
 * affordance, not the gate.
 */
export const isUjpManager = (email?: string | null): boolean => {
  if (!email) return false;
  return MANAGERS.has(email.trim().toLowerCase());
};

/** True when no approver is configured — nobody can approve (fail closed). */
export const isUjpApproverListEmpty = (): boolean => APPROVERS.size === 0;

/** The configured addresses, for a "who can do this?" hint in the UI. */
export const ujpApproverEmails = (): string[] => Array.from(APPROVERS).sort();
